import { useState } from 'react';

import { LOGISTICS_TYPES, OWNER_TYPES } from '~/constants/common';

import { accountStorage } from './accountStorage';
import type { TypeTestAccount } from './types';

import styles from './AccountSwitcherSubmenu.module.scss';

const OWNER_TYPE_OPTIONS = Object.values(OWNER_TYPES);
const LOGISTICS_OPTIONS = Object.values(LOGISTICS_TYPES);

type Props = {
  account?: TypeTestAccount;
  onSave: () => void;
  onCancel: () => void;
};

const AccountForm = ({ account, onSave, onCancel }: Props) => {
  const isEdit = !!account;

  const [loginId, setLoginId] = useState(account?.loginId ?? '');
  const [password, setPassword] = useState(account?.password ?? '');
  const [ownerType, setOwnerType] = useState<TypeTestAccount['ownerType']>(account?.ownerType ?? OWNER_TYPES['3PL']);
  const [logisticsTypes, setLogisticsTypes] = useState<TypeTestAccount['logisticsTypes']>(
    account?.logisticsTypes ?? []
  );

  const toggleLogistics = (type: (typeof LOGISTICS_OPTIONS)[number]) => {
    setLogisticsTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  const handleSubmit = () => {
    const trimmedId = loginId.trim();
    if (!trimmedId || !password) return;

    accountStorage.saveOne({
      loginId: trimmedId,
      password,
      ownerType,
      logisticsTypes,
      memo: account?.memo,
    });
    onSave();
  };

  const canSubmit = loginId.trim().length > 0 && password.length > 0;

  return (
    <div className={styles['form']}>
      <label className={styles['field']}>
        <span className={styles['field-label']}>로그인 ID</span>
        <input
          type="text"
          className={styles['field-input']}
          value={loginId}
          onChange={(e) => setLoginId(e.target.value)}
          disabled={isEdit}
          placeholder="test-account-01"
        />
      </label>

      <label className={styles['field']}>
        <span className={styles['field-label']}>비밀번호</span>
        <input
          type="password"
          className={styles['field-input']}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
      </label>

      <fieldset className={styles['fieldset']}>
        <legend className={styles['field-label']}>권한 (ownerType)</legend>
        <div className={styles['radio-group']}>
          {OWNER_TYPE_OPTIONS.map((type) => (
            <label key={type} className={styles['radio']}>
              <input type="radio" name="ownerType" checked={ownerType === type} onChange={() => setOwnerType(type)} />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles['fieldset']}>
        <legend className={styles['field-label']}>Logistics (선택)</legend>
        <div className={styles['checkbox-group']}>
          {LOGISTICS_OPTIONS.map((type) => (
            <label key={type} className={styles['checkbox']}>
              <input type="checkbox" checked={logisticsTypes.includes(type)} onChange={() => toggleLogistics(type)} />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className={styles['form-actions']}>
        <button type="button" className={styles['btn-secondary']} onClick={onCancel}>
          취소
        </button>
        <button type="button" className={styles['btn-primary']} disabled={!canSubmit} onClick={handleSubmit}>
          저장
        </button>
      </div>
    </div>
  );
};

export default AccountForm;
